import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setAdminHold, clearAdminHold, loadReservationsForLayout, listHoldGroups, clearHoldGroupByName, clearExpiredHolds } from '../services/reservationsService';
import { getEvent, listEvents } from '../services/eventsService';
import { getUserProfile } from '../services/profilesService';
import { getLayoutPublic } from '../services/layoutsService';
import { useAuth } from '../context/AuthContext';
import './ProfilePage.css';

const AdminHolds = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [eventId, setEventId] = useState('');
  const [layoutId, setLayoutId] = useState('');
  const [eventName, setEventName] = useState('');
  const [layoutName, setLayoutName] = useState('');
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [publishedEvents, setPublishedEvents] = useState([]); // [{eventId, eventName, layoutId, layoutName}]
  const [layoutCells, setLayoutCells] = useState([]);
  const [levels, setLevels] = useState([]);
  const [layoutCustomLevels, setLayoutCustomLevels] = useState({});
  const [selectedLevel, setSelectedLevel] = useState('');
  const [availabilityMap, setAvailabilityMap] = useState({}); // boothId -> status
  const [boothIdsText, setBoothIdsText] = useState('');
  const [boothIdsDisplayText, setBoothIdsDisplayText] = useState(''); // user-facing display (e.g. "12 (R1C1)")
  const [rawBoothInput, setRawBoothInput] = useState('');
  const [selectedBooths, setSelectedBooths] = useState([]); // [{id, number, level, cornerFlag, status}]
  const [reason, setReason] = useState('');
  const [holdName, setHoldName] = useState('');
  const [editingGroup, setEditingGroup] = useState(''); // non-empty when we've loaded an existing group for editing
  const [originalGroupBoothIds, setOriginalGroupBoothIds] = useState([]);
  const [expiresAt, setExpiresAt] = useState(''); // "YYYY-MM-DDTHH:mm" (local)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Show top messages in popup alerts and clear them so they don't persist inline
  useEffect(() => {
    if (error) {
      try { alert(error); } catch (e) {}
      setError('');
    }
  }, [error]);
  useEffect(() => {
    if (success) {
      try { alert(success); } catch (e) {}
      setSuccess('');
    }
  }, [success]);
  // Internal parsing: reads the internal ID string (space/comma separated IDs)
  const parseIds = () => boothIdsText.split(/\s*,\s*|\s+/).map(s => s.trim()).filter(Boolean);
  const boothIds = useMemo(() => parseIds(), [boothIdsText]);

  // Helper: format a list of IDs into a user-friendly display using cell.number when available
  const sortIds = (ids) => {
    return (ids || []).slice().sort((a, b) => String(a).localeCompare(String(b)));
  };

  const formatDisplayFromIds = (ids) => {
    const sorted = sortIds(ids);
    return (sorted || []).map(id => {
      const cell = layoutCells.find(c => c.id === id) || {};
      const num = (cell.number != null) ? String(cell.number) : (cell.boothNumber != null ? String(cell.boothNumber) : null);
      return num ? num : id;
    }).join(' ');
  };

  // Format a single id to its booth number (or fallback to id)
  const formatIdToNumber = (id) => {
    const cell = layoutCells.find(c => c.id === id) || {};
    const num = (cell.number != null) ? String(cell.number) : (cell.boothNumber != null ? String(cell.boothNumber) : null);
    return num || id;
  };

  const formatIdsList = (ids) => (ids || []).map(i => formatIdToNumber(i)).join(', ');

  const buildBoothObj = (id) => {
    const cell = layoutCells.find(c => c.id === id) || {};
    const number = (cell.number != null) ? String(cell.number) : (cell.boothNumber != null ? String(cell.boothNumber) : null);
    const level = cell.level || '';
    const cornerFlag = (cell.corner === 'Y' || cell.corner === 'y' || cell.position === 'CORNER') ? 'CORNER' : 'INLINE';
    const statusRaw = availabilityMap[id];
    const status = statusRaw ? (typeof statusRaw === 'string' ? statusRaw : (statusRaw.status || JSON.stringify(statusRaw))) : (cell.status || 'Available');
    const holdName = statusRaw && typeof statusRaw === 'object' ? (statusRaw.holdName || statusRaw.hold_name || '') : '';
    return { id, number: number || id, level, cornerFlag, status, holdName };
  };

  // Parse user-edited display text to extract the underlying IDs.
  // Accept formats like "12 (R1C1)", "12:R1C1", or plain "R1C1".
  const parseDisplayInputToIds = (text) => {
    const tokens = (text || '').split(/\s*,\s*|\s+/).map(s => s.trim()).filter(Boolean);
    return tokens.map(t => {
      // First try to interpret token as a booth number and find matching cell
      const byNumber = layoutCells.find(c => {
        const num = (c.number != null) ? String(c.number) : (c.boothNumber != null ? String(c.boothNumber) : null);
        return num === t;
      });
      if (byNumber) return byNumber.id;
      // Otherwise accept formats like "12 (R1C1)" or "12:R1C1"
      const m = t.match(/\(([^)]+)\)$/);
      if (m) return m[1];
      const idx = t.indexOf(':');
      if (idx !== -1) return t.slice(idx + 1);
      // Fallback: treat token as an ID
      return t;
    }).filter(Boolean);
  };

  // Keep the display text in sync when internal IDs or layoutCells change
  useEffect(() => {
    try {
      const ids = parseIds();
      setBoothIdsDisplayText(formatDisplayFromIds(ids));
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boothIdsText, layoutCells]);
  const [holdGroups, setHoldGroups] = useState([]); // {holdName, count, reason, expiresAtMs, latestExpiresAtMs, expiredCount, activeCount}
  const [filterMode, setFilterMode] = useState('all'); // all|active|expired
  const [sortMode, setSortMode] = useState('name'); // name|count|expires

  // Prefill from navigation state if provided
  useEffect(() => {
    const st = location?.state || {};
    if (st.eventId && !eventId) setEventId(st.eventId);
    if (st.layoutId && !layoutId) setLayoutId(st.layoutId);
  }, [location?.state]);

  // Load list of events that have a Published layout so admin can pick any
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEventsLoading(true);
        setEventsError('');
  const all = await listEvents();
  // Only include events that are not Archived and have a linked layout
  let withLayout = (all || []).filter(ev => ev?.layoutId && (ev?.status || '').toLowerCase() !== 'archived');
        // If current user is EventAdmin, restrict to events where they are listed as an eventAdmin
        try {
          if (currentUser && currentUser.uid) {
            const prof = await getUserProfile(currentUser.uid);
            if (prof && String(prof.userType || '').toLowerCase() === 'eventadmin') {
              const uidLower = String(currentUser.uid || '').toLowerCase();
              withLayout = withLayout.filter(ev => {
                if (!Array.isArray(ev.eventAdmins)) return false;
                return ev.eventAdmins.some(a => String(a || '').toLowerCase() === uidLower);
              });
            }
          }
        } catch (e) {
          // ignore profile lookup errors and fall back to showing all published events
        }
        // Fetch layouts and keep only those with status === 'Published'
        const entries = await Promise.all(withLayout.map(async (ev) => {
          try {
            const ly = await getLayoutPublic(ev.layoutId);
            const status = (ly?.status || '').toLowerCase();
            if (status !== 'published') return null;
            return {
              eventId: ev.id,
              eventName: ev.name || ev.id,
              eventStatus: ev.status || '',
              layoutId: ly.id,
              layoutName: ly.name || ly.title || ly.id,
            };
          } catch {
            return null;
          }
        }));
        const filtered = entries.filter(Boolean).sort((a, b) => String(a.eventName).localeCompare(String(b.eventName)));
        if (!cancelled) {
          setPublishedEvents(filtered);
          // If current selection is not in the allowed list (e.g., archived), or nothing selected, pick the first
          const exists = filtered.some(p => p.eventId === eventId);
          if ((!exists || (!eventId && !layoutId)) && filtered.length > 0) {
            setEventId(filtered[0].eventId);
            setLayoutId(filtered[0].layoutId);
            setEventName(filtered[0].eventName);
            setLayoutName(filtered[0].layoutName);
          }
        }
      } catch (e) {
        if (!cancelled) setEventsError('Failed to load events.');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Intentionally exclude eventId/layoutId so we don't override user choices after initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Event and Layout metadata when IDs are available
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        setSuccess('');
        if (eventId) {
          try {
            const ev = await getEvent(eventId);
            if (!cancelled) setEventName(ev?.name || eventId);
          } catch { if (!cancelled) setEventName(eventId); }
        }
            if (layoutId) {
          try {
            const ly = await getLayoutPublic(layoutId);
            if (!cancelled) {
              setLayoutName(ly?.name || ly?.title || layoutId);
              const cells = Array.isArray(ly?.cellData) ? ly.cellData : [];
              setLayoutCells(cells);
                  // Store customLevels mapping (id -> metadata) for display
                  setLayoutCustomLevels(ly?.customLevels || {});
              // Derive levels present (exclude 'Invisible')
              const levelSet = new Set(cells.map(c => c.level).filter(l => l && l !== 'Invisible'));
              const levelList = Array.from(levelSet);
              levelList.sort();
              setLevels(levelList);
              if (!selectedLevel && levelList.length > 0) setSelectedLevel(levelList[0]);
            }
          } catch { if (!cancelled) { setLayoutName(layoutId); setLayoutCells([]); setLevels([]); setLayoutCustomLevels({}); } }
        }
        if (eventId && layoutId) {
          try {
            const map = await loadReservationsForLayout({ eventId, layoutId });
            if (!cancelled) setAvailabilityMap(map || {});
          } catch { if (!cancelled) setAvailabilityMap({}); }
          try {
            const groups = await listHoldGroups({ eventId, layoutId });
            if (!cancelled) setHoldGroups(groups || []);
          } catch { if (!cancelled) setHoldGroups([]); }
        } else {
          setAvailabilityMap({});
          setHoldGroups([]);
          setLayoutCustomLevels({});
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [eventId, layoutId]);

  const handleHold = async () => {
    setError('');
    setSuccess('');
    if (!eventId?.trim() || !layoutId?.trim()) {
      setError('Event ID and Layout ID are required.');
      return;
    }
    // derive IDs to validate: prefer selectedBooths if available, otherwise parse textbox
    const inputIds = (selectedBooths && selectedBooths.length > 0) ? selectedBooths.map(b => b.id) : parseIds();
    if (!inputIds || inputIds.length === 0) {
      setError('Enter at least one Booth ID.');
      return;
    }
    if (!holdName?.trim()) {
      setError('Hold Name is required to save and manage this hold as a group.');
      return;
    }

    // Filter eligible vs skipped
    const knownIds = new Set(layoutCells.map(c => c.id));
    const unique = Array.from(new Set(inputIds));
    const eligible = [];
    const skipped = [];
    unique.forEach(id => {
      if (!knownIds.has(id)) { skipped.push({ id, reason: 'Unknown booth ID' }); return; }
      const cell = layoutCells.find(c => c.id === id);
      if (!cell || cell.status === 'Invisible' || cell.level === 'Invisible') { skipped.push({ id, reason: 'Invisible/invalid' }); return; }
      // When editing an existing group, treat the group's original booths as available
      // because we'll clear them before applying the updated set.
      const isOriginal = editingGroup && Array.isArray(originalGroupBoothIds) && originalGroupBoothIds.indexOf(id) !== -1;
      const reserved = availabilityMap[id];
      const effectivelyReserved = (!isOriginal) && reserved;
      if (effectivelyReserved && (effectivelyReserved.status || effectivelyReserved) !== true) {
        const status = typeof effectivelyReserved === 'string' ? effectivelyReserved : effectivelyReserved.status;
        skipped.push({ id, reason: status || 'Not Available' });
        return;
      }
      if (cell.status && cell.status !== 'Available') { skipped.push({ id, reason: cell.status }); return; }
      eligible.push(id);
    });

    // If we're editing an existing group, confirm before overwriting it
    if (editingGroup) {
      const ok = window.confirm(`You are updating the existing hold group "${editingGroup}". This will replace its booths with the current list. Continue?`);
      if (!ok) return;
    }

    try {
      setSaving(true);
      if (eligible.length === 0) {
          if (skipped.length > 0) {
            const summary = skipped.map(s => `${formatIdToNumber(s.id)}: ${s.reason}`).join('; ');
            setError(`No eligible booths to put on hold. Skipped: ${summary}`);
        } else if (inputIds.length === 0) {
          setError('No booths parsed from input. Enter booth numbers or IDs.');
        } else {
          setError('No eligible booths to put on hold.');
        }
        console.warn('No eligible booths', { inputIds, layoutCells, availabilityMap, skipped });
        return;
      }

      // If we loaded an existing group, clear its previous booths first so the result matches the edited values
      if (editingGroup) {
        try {
            // Always clear the previous hold group by name so renames properly remove the old group
            await clearHoldGroupByName({ eventId, layoutId, holdName: editingGroup });
            // Also attempt to clear any remaining booth docs for the original IDs (best-effort)
            if (Array.isArray(originalGroupBoothIds) && originalGroupBoothIds.length > 0) {
              try { await clearAdminHold({ eventId, layoutId, boothIds: originalGroupBoothIds }); } catch (e) {}
            }
          } catch (e) {
            // non-fatal
          }
      }

      const toSetIds = (selectedBooths && selectedBooths.length > 0) ? selectedBooths.map(b => b.id) : eligible;
      await setAdminHold({
        eventId,
        layoutId,
        boothIds: toSetIds,
        createdBy: currentUser?.uid || 'admin',
        reason,
        holdName,
        expiresAt: expiresAt ? new Date(expiresAt) : undefined,
      });

      // Refresh reservations and groups
      try { const map = await loadReservationsForLayout({ eventId, layoutId }); setAvailabilityMap(map || {}); } catch {}
      try { const groups = await listHoldGroups({ eventId, layoutId }); setHoldGroups(groups || []); } catch {}

      if (skipped.length > 0) {
        setSuccess(`Holds set for ${eligible.length} booth(s). Skipped ${skipped.length}: ${skipped.map(s=>formatIdToNumber(s.id)).join(', ')}`);
      } else {
        const setCount = (selectedBooths && selectedBooths.length > 0) ? selectedBooths.length : eligible.length;
        setSuccess(editingGroup ? `Updated hold "${editingGroup}" (${setCount} booth(s)).` : `Holds set for ${setCount} booth(s).`);
      }

      // Clear form fields after success
      setBoothIdsText('');
      setBoothIdsDisplayText('');
      setSelectedBooths([]);
      setEditingGroup('');
      setOriginalGroupBoothIds([]);
      setHoldName('');
      setReason('');
      setExpiresAt('');
    } catch (e) {
      setError('Failed to set holds: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setError('');
    setSuccess('');
    if (!eventId?.trim() || !layoutId?.trim()) {
      setError('Event ID and Layout ID are required.');
      return;
    }
    if (boothIds.length === 0) {
      setError('Enter at least one Booth ID to clear.');
      return;
    }
    try {
      setSaving(true);
      await clearAdminHold({ eventId, layoutId, boothIds });
      setSuccess(`Cleared holds for ${boothIds.length} booth(s).`);
      // Refresh reservations and groups
      try {
        const map = await loadReservationsForLayout({ eventId, layoutId });
        setAvailabilityMap(map || {});
      } catch {}
      try {
        const groups = await listHoldGroups({ eventId, layoutId });
        setHoldGroups(groups || []);
      } catch {}
      // Clear form fields after success
      setBoothIdsText('');
      setBoothIdsDisplayText('');
      setEditingGroup('');
      setOriginalGroupBoothIds([]);
      setHoldName('');
      setReason('');
      setExpiresAt('');
    } catch (e) {
      setError('Failed to clear holds: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const layoutBoothsByLevel = useMemo(() => {
    const map = {};
    for (const c of layoutCells) {
      const lvl = c.level || '';
      if (!lvl || lvl === 'Invisible') continue;
      if (!map[lvl]) map[lvl] = [];
      map[lvl].push(c);
    }
    return map;
  }, [layoutCells]);

  const getAvailableBoothIdsForLevel = (lvl) => {
    const cells = layoutBoothsByLevel[lvl] || [];
    const ids = [];
    for (const c of cells) {
      if (c.status === 'Invisible' || c.level === 'Invisible') continue;
      const res = availabilityMap[c.id];
      // Available if not in reservations map
      if (!res) {
        ids.push(c.id);
        continue;
      }
      // If reservation exists and it's an active Pending, skip it (do not include)
      const status = typeof res === 'string' ? res : (res.status || '');
      const isActivePending = (() => {
        if (status !== 'Pending') return false;
        // If expiresAt provided, check expiry; otherwise assume active (not expired)
        try {
          const ts = (typeof res === 'object' && res) ? res.expiresAt : null;
          if (!ts) return true; // no expiry info -> assume active
          if (typeof ts.toMillis === 'function') return ts.toMillis() > Date.now();
          if (ts && typeof ts.seconds === 'number') return (ts.seconds * 1000) > Date.now();
          if (typeof ts === 'number') return ts > Date.now();
          const d = new Date(ts);
          return !isNaN(d.getTime()) ? (d.getTime() > Date.now()) : true;
        } catch (e) { return true; }
      })();
      if (isActivePending) continue;
      // If we're editing an existing group, include booths that are on-hold by that same group.
      // availabilityMap entries may be objects with a holdName, or may not contain the group name at all.
      if (editingGroup) {
        let reservedHold = '';
        if (res && typeof res === 'object') reservedHold = (res.holdName || res.hold_name || '');
        // Fallback: check holdGroups (each group may list boothIds) to find which group holds this booth
        if (!reservedHold && Array.isArray(holdGroups)) {
          for (const g of holdGroups) {
            if (g && Array.isArray(g.boothIds) && g.boothIds.indexOf(c.id) !== -1) {
              reservedHold = g.holdName || '';
              break;
            }
          }
        }
        if (reservedHold && reservedHold === editingGroup) ids.push(c.id);
      }
    }
    return ids;
  };

  const addAllByLevel = () => {
    if (!selectedLevel) return;
    const idsToAdd = getAvailableBoothIdsForLevel(selectedLevel);
    if (idsToAdd.length === 0) {
      const name = (layoutCustomLevels && layoutCustomLevels[selectedLevel] && layoutCustomLevels[selectedLevel].name) ? layoutCustomLevels[selectedLevel].name : selectedLevel;
      // Show a popup alert instead of inline message
      alert(`No available booths to add for level "${name}".`);
      setSuccess('');
      return;
    }
    // Merge into selectedBooths (do not dump into the edit textarea)
    const existingIds = new Set(selectedBooths.map(b => b.id));
    const merged = selectedBooths.slice();
    idsToAdd.forEach(id => {
      if (!existingIds.has(id)) merged.push(buildBoothObj(id));
    });
    const sortedMerged = sortIds(merged.map(b => b.id));
    // Preserve existing holdName values when rebuilding objects
    const existingHoldMap = (selectedBooths || []).reduce((m, b) => { if (b && b.id && b.holdName) m[b.id] = b.holdName; return m; }, {});
    const rebuilt = sortedMerged.map(id => {
      const obj = buildBoothObj(id);
      if (existingHoldMap[id]) obj.holdName = existingHoldMap[id];
      return obj;
    });
    // update the selectedBooths list only. Do NOT update the Booth IDs input field
    setSelectedBooths(rebuilt);
    setSuccess(`Added ${idsToAdd.length} booth(s) for level "${selectedLevel}".`);
  };

  const removeAllByLevel = () => {
    if (!selectedLevel) return;
    const cells = layoutBoothsByLevel[selectedLevel] || [];
    const idsToRemove = new Set(cells.map(c => c.id));
    const filtered = boothIds.filter(id => !idsToRemove.has(id));
    const removedCount = boothIds.length - filtered.length;
    const sortedFiltered = sortIds(filtered);
    setBoothIdsText(sortedFiltered.join(' '));
    // remove from selectedBooths as well
    setSelectedBooths(prev => prev.filter(b => !idsToRemove.has(b.id)).sort((a,b)=>String(a.id).localeCompare(String(b.id))));
    setBoothIdsDisplayText(formatDisplayFromIds(sortedFiltered));
    const name = (layoutCustomLevels && layoutCustomLevels[selectedLevel] && layoutCustomLevels[selectedLevel].name) ? layoutCustomLevels[selectedLevel].name : selectedLevel;
    setSuccess(removedCount > 0 ? `Removed ${removedCount} booth(s) for level "${name}".` : 'No matching booth IDs to remove.');
  };

  // Insert booths typed into the Booth IDs field (display numbers or IDs) into the selectedBooths list
  const insertBoothsFromField = () => {
    setError('');
    setSuccess('');
    const ids = parseDisplayInputToIds(rawBoothInput || boothIdsDisplayText || boothIdsText || '');
    if (!ids || ids.length === 0) {
      setError('No booth numbers/IDs found to insert.');
      return;
    }
    const knownIds = new Set(layoutCells.map(c => c.id));
    const unknown = [];
    const toAdd = [];
    const existing = new Set(selectedBooths.map(b => b.id));
    const conflicts = [];
    ids.forEach(id => {
      if (!knownIds.has(id)) { unknown.push(id); return; }
      // detect reservations/holds under a different name
      const reserved = availabilityMap[id];
      const status = typeof reserved === 'string' ? reserved : (reserved?.status || '');
      const reservedHold = reserved && typeof reserved === 'object' ? (reserved.holdName || reserved.hold_name || '') : '';
      const currentGroupName = editingGroup || (holdName || '').trim();
      // If held by another named hold, treat as conflict
      if (reservedHold && reservedHold !== currentGroupName) {
        conflicts.push({ id, holdName: reservedHold });
        return;
      }
      // If it's an active Pending reservation, treat as conflict (do not add)
      if (status === 'Pending') {
        try {
          const ts = (reserved && typeof reserved === 'object') ? reserved.expiresAt : null;
          const pendingActive = (() => {
            if (!ts) return true; // assume active when unknown
            if (typeof ts.toMillis === 'function') return ts.toMillis() > Date.now();
            if (ts && typeof ts.seconds === 'number') return (ts.seconds * 1000) > Date.now();
            if (typeof ts === 'number') return ts > Date.now();
            const d = new Date(ts);
            return !isNaN(d.getTime()) ? (d.getTime() > Date.now()) : true;
          })();
          if (pendingActive) { conflicts.push({ id, holdName: 'Pending' }); return; }
        } catch (e) { conflicts.push({ id, holdName: 'Pending' }); return; }
      }
      if (existing.has(id)) return; // skip duplicates
      toAdd.push(id);
    });
    if (unknown.length > 0) {
      // show unknowns but continue with others
      alert(`Unknown booth IDs: ${formatIdsList(unknown)}. They were ignored.`);
    }
    if (conflicts.length > 0) {
      const msg = conflicts.map(c => `${formatIdToNumber(c.id)} (held by "${c.holdName}")`).join('; ');
      alert(`Some booths are on hold under a different group and were not added: ${msg}`);
    }
    if (toAdd.length === 0) {
      if (unknown.length === 0 && conflicts.length === 0) setSuccess('No new booths to insert.');
      return;
    }
    const merged = (selectedBooths || []).slice();
    toAdd.forEach(id => merged.push(buildBoothObj(id)));
    const sortedMergedIds = sortIds(merged.map(b => b.id));
    // Preserve existing holdName values when rebuilding objects
    const existingHoldMap = (selectedBooths || []).reduce((m, b) => { if (b && b.id && b.holdName) m[b.id] = b.holdName; return m; }, {});
    const sortedMerged = sortedMergedIds.map(id => {
      const obj = buildBoothObj(id);
      if (existingHoldMap[id]) obj.holdName = existingHoldMap[id];
      return obj;
    });
    setSelectedBooths(sortedMerged);
    // Update internal IDs and display used elsewhere, then clear the raw textarea
    setBoothIdsText(sortedMergedIds.join(' '));
    setBoothIdsDisplayText(formatDisplayFromIds(sortedMergedIds));
    setSuccess(`Inserted ${toAdd.length} booth(s) into the list.`);
    // Clear the raw input to avoid re-inserting accidentally
    setRawBoothInput('');
  };

  const handleLoadGroup = (g) => {
    if (!g) return;
    // Populate Hold Name and Booth IDs; set Reason if available
    setHoldName(g.holdName || '');
    if (Array.isArray(g.boothIds)) {
      const sortedGroup = sortIds(g.boothIds);
      // populate selectedBooths for display/editing but do NOT write into the Booth IDs textarea
      const objs = sortedGroup.map(id => {
        const o = buildBoothObj(id);
        if (g.holdName) o.holdName = g.holdName;
        return o;
      });
      setSelectedBooths(objs);
      // Clear manual input when loading a group so the textarea doesn't show stale values
      setRawBoothInput('');
      setOriginalGroupBoothIds(Array.isArray(g.boothIds) ? g.boothIds.slice() : []);
    }
    // Mark that we are editing this existing group
    setEditingGroup(g.holdName || '');
    if (g.reason) setReason(g.reason);
    // Do not show a popup when loading a group; keep UI silent
  };

  const displayGroups = useMemo(() => {
    const now = Date.now();
    let arr = Array.isArray(holdGroups) ? [...holdGroups] : [];
    // Filter
    arr = arr.filter(g => {
      // Consider group expired only if all items with an expiry have expired and none remain active
      const latest = typeof g.latestExpiresAtMs === 'number' ? g.latestExpiresAtMs : undefined;
      const hasExpiry = typeof latest === 'number';
      const groupExpired = hasExpiry ? (latest <= now && (g.activeCount || 0) === 0) : false;
      if (filterMode === 'active') return !groupExpired;
      if (filterMode === 'expired') return groupExpired;
      return true;
    });
    // Sort
    if (sortMode === 'count') {
      arr.sort((a, b) => (b.count || 0) - (a.count || 0));
    } else if (sortMode === 'expires') {
      const norm = (ms) => (typeof ms === 'number' ? ms : Number.POSITIVE_INFINITY);
      arr.sort((a, b) => norm(a.expiresAtMs) - norm(b.expiresAtMs));
    } else {
      arr.sort((a, b) => String(a.holdName || '').localeCompare(String(b.holdName || '')));
    }
    return arr;
  }, [holdGroups, filterMode, sortMode]);

  const handleRemoveGroup = async (groupName) => {
    setError('');
    setSuccess('');
    if (!eventId?.trim() || !layoutId?.trim()) { setError('Event/Layout required.'); return; }
    if (!groupName) { setError('Missing Hold Group name.'); return; }
    try {
      setSaving(true);
      const count = await clearHoldGroupByName({ eventId, layoutId, holdName: groupName });
      setSuccess(`Removed group \"${groupName}\" (${count} booth(s)).`);
      // Refresh
      try { const map = await loadReservationsForLayout({ eventId, layoutId }); setAvailabilityMap(map || {}); } catch {}
      try { const groups = await listHoldGroups({ eventId, layoutId }); setHoldGroups(groups || []); } catch {}
      // Clear form fields after success
      setBoothIdsText('');
      setBoothIdsDisplayText('');
      setHoldName('');
      setReason('');
      setExpiresAt('');
      setEditingGroup('');
    } catch (e) {
      setError('Failed to remove group: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const openHoldInBazaar = async (g) => {
    setError('');
    setSuccess('');
    if (!eventId?.trim() || !layoutId?.trim()) {
      alert('Event ID or Layout ID missing.');
      return;
    }
    try {
      const layout = await getLayoutPublic(layoutId);
      if (!layout) {
        alert('Layout not found for this event.');
        return;
      }
      if ((layout.status || '').toLowerCase() !== 'published') {
        alert('Registration is not available yet. Please check back when the layout is published.');
        return;
      }
      let ev = null;
      try { ev = await getEvent(eventId); } catch {}
      navigate('/booth-selection', { state: { existingLayout: layout, event: ev || { id: eventId }, backTo: '/admin/holds' } });
    } catch (e) {
      console.error('Failed to open booth selection:', e);
      alert('Unable to open booth selection for this event. Please try again later.');
    }
  };

  const handleCleanExpired = async () => {
    setError('');
    setSuccess('');
    if (!eventId?.trim() || !layoutId?.trim()) { setError('Event/Layout required.'); return; }
    try {
      setSaving(true);
      const count = await clearExpiredHolds({ eventId, layoutId });
      setSuccess(count > 0 ? `Cleaned ${count} expired hold(s).` : 'No expired holds found.');
      try { const map = await loadReservationsForLayout({ eventId, layoutId }); setAvailabilityMap(map || {}); } catch {}
      try { const groups = await listHoldGroups({ eventId, layoutId }); setHoldGroups(groups || []); } catch {}
    } catch (e) {
      setError('Failed to clean expired holds: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = () => {
    setError('');
    setSuccess('');
    setBoothIdsText('');
    setBoothIdsDisplayText('');
    setRawBoothInput('');
    setSelectedBooths([]);
    setHoldName('');
    setReason('');
    setExpiresAt('');
    setEditingGroup('');
    setOriginalGroupBoothIds([]);
    setSuccess('Cleared all fields.');
  };

  // Remove a single booth from the selected list; if we're editing an existing group,
  // also release the hold for that booth immediately.
  const handleRemoveBooth = async (boothId) => {
    setError('');
    setSuccess('');
    if (!boothId) return;
    if (editingGroup) {
      try {
        setSaving(true);
        await clearAdminHold({ eventId, layoutId, boothIds: [boothId] });
        // Refresh availability and groups
        try { const map = await loadReservationsForLayout({ eventId, layoutId }); setAvailabilityMap(map || {}); } catch {}
        try { const groups = await listHoldGroups({ eventId, layoutId }); setHoldGroups(groups || []); } catch {}
        // Remove from UI lists
        setSelectedBooths(prev => prev.filter(x => x.id !== boothId));
        setOriginalGroupBoothIds(prev => (Array.isArray(prev) ? prev.filter(id => id !== boothId) : []));
        setBoothIdsText(prev => prev.split(/\s+/).filter(s => s && s !== boothId).join(' '));
        setSuccess(`Released hold for ${formatIdToNumber(boothId)}.`);
      } catch (e) {
        setError('Failed to release hold: ' + (e?.message || e));
      } finally {
        setSaving(false);
      }
    } else {
      // just remove from the local selected list
      setSelectedBooths(prev => prev.filter(x => x.id !== boothId));
      setBoothIdsText(prev => prev.split(/\s+/).filter(s => s && s !== boothId).join(' '));
      setSuccess('Removed booth from selection.');
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
      </div>
  <h2 className="profile-title">Admin: Booth Holds</h2>
      <div className="profile-hint" style={{ marginBottom: 10 }}>Put booths On-Hold to temporarily block vendor selection. Enter multiple Booth IDs separated by spaces or commas.</div>

      {error && <div className="profile-error">{error}</div>}
      {success && <div className="profile-hint" style={{ color: '#2e7d32', marginBottom: 12 }}>{success}</div>}

      <div className="profile-grid" style={{ marginTop: 8 }}>
        <div>
          <label className="profile-label">Event</label>
          <select
            className="profile-input"
            value={eventId || ''}
            onChange={(e) => {
              const val = e.target.value;
              const opt = publishedEvents.find(p => p.eventId === val);
              setEventId(val);
              // Reset booth ID editor when switching events
              setBoothIdsText('');
              setBoothIdsDisplayText('');
              setEditingGroup('');
              setOriginalGroupBoothIds([]);
              setSelectedBooths([]);
              if (opt) {
                setEventName(opt.eventName);
                setLayoutId(opt.layoutId);
                setLayoutName(opt.layoutName);
              } else {
                setEventName(val);
              }
              setSuccess('');
              setError('');
            }}
            disabled={eventsLoading || publishedEvents.length === 0}
          >
            {eventsLoading && <option value="">Loading events…</option>}
            {!eventsLoading && publishedEvents.length === 0 && <option value="">No published events found</option>}
            {!eventsLoading && publishedEvents.length > 0 && (
              <>
                <option value="" disabled>Select an event…</option>
                {publishedEvents.map(p => (
                  <option key={p.eventId} value={p.eventId}>
                    {p.eventName}{p.eventStatus ? ` (${p.eventStatus})` : ''}
                  </option>
                ))}
              </>
            )}
          </select>
          {eventsError && <div className="profile-hint" style={{ color: '#b71c1c' }}>{eventsError}</div>}
          {eventId && <div className="profile-hint">ID: {eventId}</div>}
        </div>
        <div>
          <label className="profile-label">Layout</label>
          <input className="profile-input" value={layoutName || (layoutId || '')} readOnly />
          {layoutId && <div className="profile-hint">ID: {layoutId}</div>}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
            <label className="profile-label" style={{ marginBottom: 0 }}>Saved Hold Groups</label>
            <button type="button" className="cancel-btn" onClick={handleCleanExpired} disabled={saving} title="Delete expired holds for this event/layout">Clean Expired Holds</button>
          </div>
          {holdGroups.length === 0 ? (
            <div className="profile-hint">No hold groups found for this event/layout.</div>
          ) : (
            <div>
              {/* Filter and Sort Controls */}
              <div className="profile-actions" style={{ justifyContent: 'flex-start', gap: 8, marginBottom: 8 }}>
                <div>
                  <label className="profile-label" style={{ marginBottom: 4 }}>Filter</label>
                  <select className="profile-input" value={filterMode} onChange={e => setFilterMode(e.target.value)} style={{ maxWidth: 180 }}>
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                <div>
                  <label className="profile-label" style={{ marginBottom: 4 }}>Sort By</label>
                  <select className="profile-input" value={sortMode} onChange={e => setSortMode(e.target.value)} style={{ maxWidth: 220 }}>
                    <option value="name">Name (A→Z)</option>
                    <option value="count"># Booths (High→Low)</option>
                    <option value="expires">Expiry (Soonest→Latest)</option>
                  </select>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
              <table className="profile-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Hold Name</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Reason</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}># Booths</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Expires</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Created By</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayGroups.map(g => {
                    const now = Date.now();
                    const latest = typeof g.latestExpiresAtMs === 'number' ? g.latestExpiresAtMs : undefined;
                    const hasExpiry = typeof latest === 'number' || typeof g.expiresAtMs === 'number';
                    const groupExpired = hasExpiry ? ((latest ?? g.expiresAtMs) <= now && (g.activeCount || 0) === 0) : false;
                    const exp = (g.expiresAtMs && !isNaN(g.expiresAtMs)) ? new Date(g.expiresAtMs).toLocaleString() : (hasExpiry ? '—' : 'No expiry');
                    return (
                      <tr key={g.holdName} className={groupExpired ? 'row-expired' : ''}>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{g.holdName}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{g.reason || '-'}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{g.count}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                          {groupExpired ? (
                            <span style={{ color: '#9c27b0', fontWeight: 600 }}>Expired</span>
                          ) : (
                            <span style={{ color: '#2e7d32', fontWeight: 600 }}>Active</span>
                          )}
                        </td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{exp}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{g.createdBy || '-'}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                          <button type="button" className="new-layout-btn" onClick={() => handleLoadGroup(g)} disabled={saving} title="Load booth IDs into the editor">Load</button>
                          <button type="button" className="new-layout-btn" onClick={() => openHoldInBazaar(g)} disabled={saving} title="View hold in layout">View</button>
                          <button type="button" className="cancel-btn" onClick={() => handleRemoveGroup(g.holdName)} disabled={saving} title="Remove all holds in this group">Remove</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
        <div>
          <label className="profile-label">Level</label>
          <select className="profile-input" value={selectedLevel} onChange={e => setSelectedLevel(e.target.value)} disabled={levels.length === 0}>
            {levels.length === 0 && <option value="">No levels found</option>}
            {levels.map(lvl => (
              <option key={lvl} value={lvl}>{(layoutCustomLevels && layoutCustomLevels[lvl] && layoutCustomLevels[lvl].name) ? layoutCustomLevels[lvl].name : lvl}</option>
            ))}
          </select>
          <div className="profile-actions" style={{ gap: 8, marginTop: 8 }}>
            <button type="button" className="apply-btn" onClick={addAllByLevel} disabled={!selectedLevel || saving}>Add from {selectedLevel}</button>
            <button type="button" className="cancel-btn" onClick={removeAllByLevel} disabled={!selectedLevel || saving}>Remove from {selectedLevel}</button>
          </div>
          <div className="profile-hint">Use Add/Remove to manage all booths for the selected Level.</div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label className="profile-label">Booth IDs</label>
          <textarea
            className="profile-input"
            placeholder="R1C1, R1C2 R1C3 (comma or space separated)"
            value={rawBoothInput}
            onChange={e => {
              const v = e.target.value;
              setRawBoothInput(v);
              // keep internal parsed IDs in sync for counts and processing, but do NOT reformat the textarea
              const ids = parseDisplayInputToIds(v);
              const sorted = sortIds(ids);
              setBoothIdsText(sorted.join(' '));
              // update display version used elsewhere (but not the textarea itself)
              setBoothIdsDisplayText(formatDisplayFromIds(sorted));
            }}
            rows={4}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <div className="profile-hint" style={{ margin: 0 }}>Parsed {boothIds.length} booth(s).</div>
            <div style={{ marginLeft: 'auto' }}>
              <button type="button" className="apply-btn" onClick={insertBoothsFromField} disabled={saving || !(boothIdsDisplayText && boothIdsDisplayText.trim())}>Add to the list</button>
            </div>
          </div>
          {selectedBooths.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 6 }}>Number</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>ID</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Level</th>
                      <th style={{ textAlign: 'left', padding: 6 }}>Type</th>
                      <th style={{ textAlign: 'left', padding: 6 }}>Hold</th>
                      <th style={{ textAlign: 'left', padding: 6 }}>Status</th>
                    <th style={{ textAlign: 'left', padding: 6 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBooths.map(b => (
                    <tr key={b.id}>
                      <td style={{ padding: 6, borderTop: '1px solid #eee' }}>{b.number}</td>
                      <td style={{ padding: 6, borderTop: '1px solid #eee' }}>{b.id}</td>
                      <td style={{ padding: 6, borderTop: '1px solid #eee' }}>{b.level || '-'}</td>
                        <td style={{ padding: 6, borderTop: '1px solid #eee' }}>{b.cornerFlag}</td>
                        <td style={{ padding: 6, borderTop: '1px solid #eee' }}>{b.holdName || '-'}</td>
                        <td style={{ padding: 6, borderTop: '1px solid #eee' }}>{b.status}</td>
                      <td style={{ padding: 6, borderTop: '1px solid #eee' }}>
                        <button type="button" className="cancel-btn" onClick={() => handleRemoveBooth(b.id)} disabled={saving}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            boothIds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {boothIds.map(id => (
                  <span key={id} style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 12, padding: '2px 8px', fontSize: 12 }}>{formatDisplayFromIds([id])}</span>
                ))}
              </div>
            )
          )}
        </div>
        <div>
          <label className="profile-label">Hold Name</label>
          <input className="profile-input" placeholder="e.g. Sponsor Block A" value={holdName} onChange={e => setHoldName(e.target.value)} />
        </div>
        <div>
          <label className="profile-label">Reason (optional)</label>
          <input className="profile-input" placeholder="e.g. Sponsor allocation" value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div>
          <label className="profile-label">Expires At (optional)</label>
          <input type="datetime-local" className="profile-input" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
          <div className="profile-hint">If set, holds automatically expire around this time.</div>
        </div>
      </div>

      <div className="profile-actions">
        <button className="apply-btn" onClick={handleHold} disabled={saving || !eventId?.trim() || !layoutId?.trim() || ((selectedBooths.length === 0) && (boothIds.length === 0)) || !holdName?.trim()}>{saving ? 'Applying…' : (editingGroup ? 'Update Hold' : 'Set On-Hold')}</button>
        <button className="cancel-btn" onClick={handleClearAll} disabled={saving} title="Reset all fields">Clear List</button>
      </div>

      <div className="profile-meta">Note: Firestore security rules must allow admin-only hold writes.</div>
    </div>
  );
};

export default AdminHolds;
